import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import api from '../../api';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import VotacaoViewModel from '../../viewmodel/VotacaoViewModel';
import PaginationComponent from '../../components/PaginationComponent';

interface VotoInfo {
    voto: string;
    justificativa?: string;
    dataHora: string;
}

interface MembroVotacao {
    membroId: string;
    nome: string;
    papel: string;
    voto?: VotoInfo;
}

interface CandidatoInfo {
    id: string;
    candidatoId: string;
    nome: string;
    nomeSocial?: string;
    inscricao: string;
}

interface CandidatoDisponivel {
    id: string;
    nome: string;
    nomeSocial?: string;
    inscricao: string;
}

interface VotacaoAdminData {
    candidato: CandidatoInfo;
    membros: MembroVotacao[];
    podeFechar: boolean;
}

const VotacaoAdmin: React.FC = () => {
    const { bancaId } = useParams<{ bancaId: string }>();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [dados, setDados] = useState<VotacaoAdminData | null>(null);
    const [candidatosDisponiveis, setCandidatosDisponiveis] = useState<CandidatoDisponivel[]>([]);
    const [loading, setLoading] = useState(true);
    const [votos, setVotos] = useState<Record<string, { voto: string; justificativa: string }>>({});
    const [paginaNaoIniciada, setPaginaNaoIniciada] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        setBreadcrumbs([
            { path: '/', breadcrumb: '🏠' },
            { path: `/bancas/${bancaId}/votacao-admin`, breadcrumb: 'Admin - Votação' },
        ]);
    }, [bancaId, setBreadcrumbs]);

    const carregarDados = async () => {
        try {
            const response = await api.get(`/bancas/${bancaId}/candidatos/votacao-admin`);
            setDados(response.data);
            
            setVotos(prevVotos => {
                const votosIniciais: Record<string, { voto: string; justificativa: string }> = { ...prevVotos };
                response.data.membros.forEach((m: MembroVotacao) => {
                    if (m.voto) {
                        votosIniciais[m.membroId] = {
                            voto: m.voto.voto,
                            justificativa: m.voto.justificativa || ''
                        };
                    }
                });
                return votosIniciais;
            });
        } catch (err: any) {
            if (err.response?.status === 404) {
                setDados(null);
            } else {
                toast.error(BackendErrorMessage(err));
            }
        } finally {
            setLoading(false);
        }
    };

    const carregarCandidatosDisponiveis = async () => {
        try {
            const pageSize = 10;
            const response = await api.get(`/bancas/${bancaId}/candidatos/votacao-admin-lista`, {
                params: {
                    naoIniciadaPage: paginaNaoIniciada,
                    naoIniciadaSize: pageSize,
                    fechadaPage: 0,
                    fechadaSize: 10
                }
            });
            setCandidatosDisponiveis(response.data.naoIniciada.candidatos.content || []);
            setTotalPages(response.data.naoIniciada.candidatos.totalPages || 1);
        } catch (err: any) {
            console.error('Erro ao carregar candidatos disponíveis:', err);
        }
    };

    useEffect(() => {
        carregarDados();
        carregarCandidatosDisponiveis();
    }, [bancaId, paginaNaoIniciada]);

    const handleIniciarVotacao = async (candidatoId: string) => {
        try {
            await VotacaoViewModel.patchAbrirVotacao(bancaId!, candidatoId);
            toast.success('Heteroidentificação iniciada');
            carregarDados();
            carregarCandidatosDisponiveis();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    const handleVotoChange = (membroId: string, voto: string) => {
        setVotos(prev => ({
            ...prev,
            [membroId]: { ...prev[membroId], voto }
        }));
    };

    const handleJustificativaChange = (membroId: string, justificativa: string) => {
        setVotos(prev => ({
            ...prev,
            [membroId]: { ...prev[membroId], justificativa }
        }));
    };

    const handleSalvarVoto = async (membroId: string) => {
        const votoData = votos[membroId];
        if (!votoData?.voto) {
            toast.warning('Selecione um voto');
            return;
        }

        try {
            await api.post(`/bancas/${bancaId}/candidatos/${dados?.candidato.id}/voto-admin`, {
                membroId,
                voto: votoData.voto,
                justificativa: votoData.justificativa
            });
            toast.success('Voto registrado');
            
            // Preservar os votos atuais antes de recarregar
            const votosAtuais = { ...votos };
            await carregarDados();
            
            // Restaurar os votos que não foram salvos
            setVotos(prevVotos => {
                const novosVotos = { ...votosAtuais };
                // Manter apenas os votos que ainda não foram salvos
                Object.keys(novosVotos).forEach(key => {
                    if (dados?.membros.find(m => m.membroId === key && m.voto)) {
                        delete novosVotos[key];
                    }
                });
                return novosVotos;
            });
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    const handleFecharVotacao = async () => {
        if (!dados?.podeFechar) {
            toast.warning('Todos os membros devem votar antes de fechar');
            return;
        }

        try {
            await api.patch(`/bancas/${bancaId}/candidatos/${dados.candidato.candidatoId}/votacao/fechamento`);
            toast.success('Heteroidentificação finalizada');
            carregarDados();
            carregarCandidatosDisponiveis();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    if (loading) {
        return (
            <Container className="text-center my-5">
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <Container className="my-4">
            {!dados && candidatosDisponiveis.length > 0 && (
                <Card className="mb-4">
                    <Card.Header className="bg-warning">
                        <h5 className="mb-0">Candidatos Disponíveis</h5>
                    </Card.Header>
                    <Card.Body>
                        {candidatosDisponiveis.map(candidato => (
                            <Card key={candidato.id} className="mb-2">
                                <Card.Body className="py-2">
                                    <Row className="align-items-center">
                                        <Col md={5}>
                                            <strong>{candidato.nomeSocial || candidato.nome}</strong>
                                        </Col>
                                        <Col md={4}>
                                            <Badge bg="secondary">Inscrição: {candidato.inscricao}</Badge>
                                        </Col>
                                        <Col md={3} className="text-end">
                                            <Button 
                                                size="sm" 
                                                variant="primary"
                                                onClick={() => handleIniciarVotacao(candidato.id)}
                                            >
                                                Iniciar
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        ))}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <small className="text-muted">
                                    Página {paginaNaoIniciada + 1} de {totalPages}
                                </small>
                                <PaginationComponent
                                    currentPage={paginaNaoIniciada + 1}
                                    totalPages={totalPages}
                                    onPageChange={(page) => setPaginaNaoIniciada(page - 1)}
                                />
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {!dados && candidatosDisponiveis.length === 0 && (
                <Alert variant="info">Nenhuma heteroidentificação em andamento e nenhum candidato disponível</Alert>
            )}

            {dados && (
            <>
            <Card className="mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">Candidato em Votação</h5>
                </Card.Header>
                <Card.Body>
                    <h4>{dados.candidato.nomeSocial || dados.candidato.nome}</h4>
                    <Badge bg="secondary">Inscrição: {dados.candidato.inscricao}</Badge>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header>
                    <h5 className="mb-0">Votos dos Membros</h5>
                </Card.Header>
                <Card.Body>
                    {dados.membros.map(membro => (
                        <Card key={membro.membroId} className="mb-3">
                            <Card.Body>
                                <Row>
                                    <Col md={3}>
                                        <strong>{membro.nome}</strong>
                                        <div className="text-muted small">{membro.papel}</div>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select
                                            size="sm"
                                            value={votos[membro.membroId]?.voto || ''}
                                            onChange={(e) => handleVotoChange(membro.membroId, e.target.value)}
                                            disabled={!!membro.voto}
                                        >
                                            <option value="">Selecione...</option>
                                            <option value="VALIDADO">Validado</option>
                                            <option value="NAO_VALIDADO">Não Validado</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Control
                                            size="sm"
                                            placeholder="Justificativa (obrigatória para Não Validado)"
                                            value={votos[membro.membroId]?.justificativa || ''}
                                            onChange={(e) => handleJustificativaChange(membro.membroId, e.target.value)}
                                            disabled={!!membro.voto}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        {membro.voto ? (
                                            <Badge bg={membro.voto.voto === 'VALIDADO' ? 'success' : 'danger'}>
                                                Votado
                                            </Badge>
                                        ) : (
                                            <Button size="sm" onClick={() => handleSalvarVoto(membro.membroId)}>
                                                Salvar
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                </Card.Body>
            </Card>

            <div className="text-center mt-4">
                <Button
                    variant="danger"
                    size="lg"
                    onClick={handleFecharVotacao}
                    disabled={!dados.podeFechar}
                >
                    Finalizar Heteroidentificação
                </Button>
                {!dados.podeFechar && (
                    <div className="text-muted small mt-2">
                        Todos os membros devem votar antes de finalizar
                    </div>
                )}
            </div>
            </>
            )}
        </Container>
    );
};

export default VotacaoAdmin;
